/**
 * The AI tools' input ceiling: how many characters each tool takes in one
 * request, the browser side.
 *
 * Without a ceiling, a pasted text of any length reaches the model, is
 * read whole and is spent against the person's allowance and the
 * practice's token budget.
 *
 * The value lives in tenants.config `features.ai_input_max_chars`, an
 * object with one entry per tool or box (`email`, `med_list`,
 * `brand_formatter`, `assessment_notes`, `recipient`), each read on its own
 * with its own factory value (ADR-0009 config-seam convention). `email`
 * covers every box of the email helper: the drafter, the refiner, the
 * polish, and the draft, topic and special instructions the refiner
 * carries, and the assessment refiner's Want changes? box, which is the
 * same panel. `assessment_notes` is the notes the assessment refiner
 * carries; `recipient` the email's "Who is this for?", on the draft and as
 * the refiner carries it.
 *
 * Mirror of api/_shared/aiInputLimits.ts (the server fences the five
 * AI routes with the same reading; THIS side says the number under the
 * box and turns the button off). Pure; unit-tested on both sides and against each other.
 */

export type AiInputTool = 'email' | 'med_list' | 'brand_formatter' | 'assessment_notes' | 'recipient';

/** Platform defaults, used when the practice has not set its own. */
export const DEFAULT_AI_INPUT_MAX_CHARS: Readonly<Record<AiInputTool, number>> = {
  email: 20_000,
  med_list: 20_000,
  brand_formatter: 50_000,
  assessment_notes: 60_000,
  recipient: 500,
};

/**
 * Read one tool's ceiling from a tenants.config value. Anything short of a
 * positive integer falls back to that tool's own factory value, so a typo
 * in one entry never moves another tool's ceiling.
 */
export function readAiInputMaxChars(config: unknown, tool: AiInputTool): number {
  const fallback = DEFAULT_AI_INPUT_MAX_CHARS[tool];
  if (typeof config !== 'object' || config === null) return fallback;
  const features = (config as Record<string, unknown>).features;
  if (typeof features !== 'object' || features === null) return fallback;
  const entries = (features as Record<string, unknown>).ai_input_max_chars;
  if (typeof entries !== 'object' || entries === null || Array.isArray(entries)) return fallback;
  const v = (entries as Record<string, unknown>)[tool];
  return typeof v === 'number' && Number.isSafeInteger(v) && v > 0 ? v : fallback;
}
